import { IpaType, isStress, syllableBreak } from "../phonemes/ipa";

export type StressType = 'stressed' | 'unstressed' | 'any';

export interface SyllableContext {
  /** current syllable is stressed, unstressed, the word's first or last syllable */
  readonly current: StressType | 1 | -1;
  /** whether or not previous syllables should be stressed */
  readonly prev?: StressType[];
  /** whether or not following syllables should be stressed */
  readonly next?: StressType[];
}

/**
 * check the syllable types relative to ipa[i], assumes syllable breaks & stress markers.
 * @param ipa segmented IPA that includes syllable breaks & stress markers
 * @param i the phoneme in ipa[] to start checks from
 * @param context what kind of syllable we want ipa[i] to be in
 * @returns true if ipa[i] matches the given syllable context
 */
export function checkSyllableContext(ipa: IpaType[], i: number, context: SyllableContext): boolean {
  // check current syllable
  switch (context.current) {
    case 1: if (!isInFirst(ipa, i)) return false; break;
    case -1: if (!isInLast(ipa, i)) return false; break;
    default: if (!hasStressType(ipa, i, context.current)) return false; break;
  }
  // check previous syllables
  if (context.prev) {
    let curr = i;
    for (const type of context.prev) {
      const prev = findPrevious(ipa, curr);
      if (prev === false) {
        return false;
      }
      if (!hasStressType(ipa, prev, type)) {
        return false;
      }
      curr = prev;
    }
  }
  // check next syllables
  if (context.next) {
    let curr = i;
    for (const type of context.next) {
      const next = findNext(ipa, curr);
      if (next === false) {
        return false;
      }
      if (!hasStressType(ipa, next, type)) {
        return false;
      }
      curr = next;
    }
  }
  return true;
}

function isInFirst(ipa: IpaType[], i: number): boolean {
  const next = ipa.indexOf(syllableBreak);
  return next === -1 || i < next;
}

function isInLast(ipa: IpaType[], i: number): boolean {
  const prev = ipa.lastIndexOf(syllableBreak);
  return prev < i;
}

/** check if ipa[i] is in a syllable of the given stress type */
function hasStressType(ipa: IpaType[], i: number, type: StressType): boolean {
  switch (type) {
    case 'stressed': return isInStress(ipa, i);
    case 'unstressed': return !isInStress(ipa, i);
    default: return true;
  }
}

/** check if ipa[i] is in a stressed syllable */
function isInStress(ipa: IpaType[], i: number): boolean {
  const prev = ipa.lastIndexOf(syllableBreak, i);
  const next = ipa.indexOf(syllableBreak, i);
  return syllableHasStress(ipa, prev, next);
}

function syllableHasStress(ipa: IpaType[], start: number, end: number): boolean {
  start = start === -1 ? 0 : start;
  end = end === -1 ? ipa.length : end;
  for (let i = start; i < end; i++) {
    if (isStress(ipa[i])) {
      return true;
    }
  }
  return false;
}

/** get an index to a symbol in the previous syllable */
function findPrevious(ipa: IpaType[], i: number): number | false {
  for (let j = i; j >= 0; j--) {
    if (ipa[j] === syllableBreak) {
      return j - 1;
    }
  }
  return false;
}

/** get an index to a symbol in the next syllable */
function findNext(ipa: IpaType[], i: number): number | false {
  for (let j = i + 1; j < ipa.length; j++) {
    if (ipa[j] === syllableBreak) {
      return j + 1;
    }
  }
  return false;
}
